import { Secret } from '../OrchestrationSecrets/types';

export enum FabricOAuthType {
  U2M = 'u2m',
  M2M = 'm2m'
}

export enum ORCHESTRATION_PROVIDER {
  Databricks = 'databricks',
  Snowflake = 'snowflake',
  ProphecyManaged = 'ProphecyManaged',
  BigQuery = 'bigquery'
}

export enum OrchestrationDbxAuthType {
  PAT = 'pat',
  OAUTH = 'oauth'
}

export type OrchestrationFabricInfo = {
  url: string;
};

export type OrchestrationDataBricksProvider = OrchestrationFabricInfo & {
  providerType: ORCHESTRATION_PROVIDER.Databricks | ORCHESTRATION_PROVIDER.ProphecyManaged;
};

export type OrchestrationProviderInfo = OrchestrationDataBricksProvider;

export type OrchestrationProvider<T = OrchestrationDataBricksProvider> = {
  providerInfo: T;
};

export type OrchestratorKgConfig<AuthProperties> = {
  kgConfig: {
    enabled: boolean;
    authProperties: AuthProperties;
    kgSchedule: {
      cron?: string;
      timeZone?: string;
    };
    useConnectorAuth: boolean;
  };
};
export type ReachabilityStatus = 'inactive' | 'reachable' | 'unreachable' | 'unknown' | 'unsupported';

export type GenericOrchestrationConnection<
  K extends string,
  P extends Record<string, unknown>,
  isDefaultWarehouseConnection extends boolean = boolean
> = {
  id: string;
  name: string;
  kind: K;
  isDefaultWarehouseConnection: isDefaultWarehouseConnection;
  properties: P;
  reachabilityStatus: ReachabilityStatus;
};

// String - SecretPayload is used for creating unified fabrics
// It is stored as "Secret" in for orch fabrics
export type SecretPayload = Secret | string;

// -------------- DATABRICKS ORCHESTRATION TYPES START --------------

export type OrchestratorDbxAuthProperties<
  A extends OrchestrationDbxAuthType = OrchestrationDbxAuthType,
  O extends FabricOAuthType = FabricOAuthType,
  SecretPayloadType extends SecretPayload = SecretPayload
> = A extends 'pat'
  ? {
      authType: OrchestrationDbxAuthType.PAT;
      token: SecretPayloadType;

      oAuthType?: never;
      clientId?: never;
      clientSecret?: never;
      oAuthAppRegistrationID?: never;
    }
  : O extends FabricOAuthType.M2M
    ? {
        authType: OrchestrationDbxAuthType.OAUTH;
        oAuthType: FabricOAuthType.M2M;
        clientId: string;
        clientSecret: SecretPayloadType;

        token?: never;
        oAuthAppRegistrationID?: never;
      }
    : {
        authType: OrchestrationDbxAuthType.OAUTH;
        oAuthType: FabricOAuthType.U2M;
        oAuthAppRegistrationID: string;

        token?: never;
        clientId?: never;
        clientSecret?: never;
      };

export type DatabricksConnectionProperties<SecretPayloadType extends SecretPayload = Secret> =
  OrchestratorDbxAuthProperties<OrchestrationDbxAuthType, FabricOAuthType, SecretPayloadType> &
    OrchestratorKgConfig<
      OrchestratorDbxAuthProperties<OrchestrationDbxAuthType, FabricOAuthType, SecretPayloadType>
    > & {
      jdbcUrl: string;
      schema: string;
      catalog: string;
      clusterId: string;
    };

export type DatabricksDefaultWarehouseConnection<SecretPayloadType extends SecretPayload = Secret> =
  GenericOrchestrationConnection<
    ORCHESTRATION_PROVIDER.Databricks,
    DatabricksConnectionProperties<SecretPayloadType>,
    true
  >;

// -------------- DATABRICKS ORCHESTRATION TYPES END --------------

// -------------- BIGQUERY ORCHESTRATION TYPES START --------------

export type OrchestrationBQAuthType = 'private_key' | 'oauth';

export type OrchestratorBQAuthProperties<
  A extends OrchestrationBQAuthType = OrchestrationBQAuthType,
  SecretPayloadType extends SecretPayload = SecretPayload
> = A extends 'private_key'
  ? {
      authType: 'private_key';
      serviceAccountKey: SecretPayloadType;
    }
  : {
      authType: 'oauth';
      oAuthAppRegistrationID: string;
    };

export type BigQueryConnectionProperties<SecretPayloadType extends SecretPayload = Secret> =
  OrchestratorBQAuthProperties<OrchestrationBQAuthType, SecretPayloadType> &
    OrchestratorKgConfig<OrchestratorBQAuthProperties<OrchestrationBQAuthType, SecretPayloadType>> & {
      projectId: string;
      datasetId: string;
      provider: string;
      bucket: string;
      kmskey: string;
    };

export type BigQueryDefaultWarehouseConnection = GenericOrchestrationConnection<
  ORCHESTRATION_PROVIDER.BigQuery,
  BigQueryConnectionProperties,
  true
>;

// -------------- BIGQUERY ORCHESTRATION TYPES END --------------

// -------------- SNOWFLAKE ORCHESTRATION TYPES START --------------

export enum OrchestrationSnowflakeAuthType {
  PWD = 'pwd',
  OAUTH = 'oauth'
}

export type OrchestratorSnowflakeAuthProperties<
  A extends OrchestrationSnowflakeAuthType = OrchestrationSnowflakeAuthType,
  O extends FabricOAuthType = FabricOAuthType,
  SecretPayloadType extends SecretPayload = SecretPayload
> = A extends OrchestrationSnowflakeAuthType.PWD
  ? {
      authType: OrchestrationSnowflakeAuthType.PWD;
      username: string;
      password: SecretPayloadType;

      oAuthType?: never;
      clientId?: never;
      clientSecret?: never;
      oAuthAppRegistrationID?: never;
    }
  : O extends FabricOAuthType.M2M
    ? {
        authType: OrchestrationSnowflakeAuthType.OAUTH;
        oAuthType: FabricOAuthType.M2M;
        clientId: string;
        clientSecret: SecretPayloadType;

        username?: never;
        password?: never;
        oAuthAppRegistrationID?: never;
      }
    : {
        authType: OrchestrationSnowflakeAuthType.OAUTH;
        oAuthType: FabricOAuthType.U2M;
        oAuthAppRegistrationID: string;

        username?: never;
        password?: never;
        clientId?: never;
        clientSecret?: never;
      };

export type SnowflakeConnectionProperties<SecretPayloadType extends SecretPayload = Secret> =
  OrchestratorSnowflakeAuthProperties<OrchestrationSnowflakeAuthType, FabricOAuthType, SecretPayloadType> & {
    account: string;
    database: string;
    schema: string;
    warehouse: string;
    role: string;
  };

export type SnowflakeDefaultWarehouseConnection<SecretPayloadType extends SecretPayload = Secret> =
  GenericOrchestrationConnection<
    ORCHESTRATION_PROVIDER.Snowflake,
    SnowflakeConnectionProperties<SecretPayloadType>,
    true
  > & {
    category: 'warehouse';
  };

// -------------- SNOWFLAKE ORCHESTRATION TYPES END --------------

export type OrchestrationDefaultWarehouseConnection<
  K extends ORCHESTRATION_PROVIDER = ORCHESTRATION_PROVIDER,
  SecretPayloadType extends SecretPayload = Secret
> = K extends ORCHESTRATION_PROVIDER.Databricks
  ? DatabricksDefaultWarehouseConnection<SecretPayloadType>
  : K extends ORCHESTRATION_PROVIDER.BigQuery
    ? BigQueryDefaultWarehouseConnection
    : K extends ORCHESTRATION_PROVIDER.Snowflake
      ? SnowflakeDefaultWarehouseConnection<SecretPayloadType>
      : never;
